import { HilPresentationType } from '@bizfirst/hil-types';
import type { HilPresentationAction, HilPresentation } from '@bizfirst/hil-types';
import type { HilChatMessage, HilChatMessageKind } from '../types/HilChatMessage';

export function resolveKind(presentationType: string): HilChatMessageKind {
  if (presentationType === HilPresentationType.Chat) return 'text';
  if (presentationType === HilPresentationType.Form) return 'form';
  // approval / info / custom (and any future backend value) fall back to a
  // generic text bubble rather than being dropped or throwing (Phase 1 requirement).
  return 'text';
}

export function splitActions(actions: HilPresentationAction[]): {
  actions: HilPresentationAction[];
  presetActions: HilPresentationAction[];
} {
  const presetActions = actions.filter(a => a.Style === 'preset');
  const nonPreset = actions.filter(a => a.Style !== 'preset');
  return { actions: nonPreset, presetActions };
}

/**
 * Pure mapping: one Presentation + its resolved actions -> one HilChatMessage.
 * `actions` must already be resolved through the reader's step->envelope Actions[]
 * fallback (i.e. pass reader.getActions(presentation.Order), not presentation.Actions).
 */
export function toChatMessage(
  presentation: HilPresentation,
  sessionID: number,
  actions: HilPresentationAction[]
): HilChatMessage {
  const { actions: nonPreset, presetActions } = splitActions(actions);
  const isGenericFallback =
    presentation.PresentationType !== HilPresentationType.Chat &&
    presentation.PresentationType !== HilPresentationType.Form;

  return {
    id: `${sessionID}-p${presentation.Order}`,
    kind: resolveKind(presentation.PresentationType),
    sender: 'system',
    order: presentation.Order,
    status: 'sent',
    sentAt: null, // no per-turn timestamp in the backend envelope — see HilChatMessage.sentAt doc
    promptMessage: isGenericFallback
      ? (presentation.PromptMessage ?? presentation.SummaryText ?? null)
      : (presentation.PromptMessage ?? null),
    formID: presentation.PresentationType === HilPresentationType.Form ? (presentation.FormID ?? null) : null,
    formSchemaJson: presentation.PresentationType === HilPresentationType.Form ? (presentation.FormSchemaJson ?? null) : null,
    prePopulate: presentation.PrePopulate ?? {},
    actions: nonPreset,
    presetActions,
  };
}
